// The step-5 (Import Control) AND step-6 (AP/AR Approval) editor — one
// component, two gates, selected by the `step` prop.
//
// At Import Control the ONLY live control is the per-line Company picker.
// Every other field — vendor, external id, dates, amounts, currency, every
// line's description/price/quantity/total, the "What is it?" picker, and
// Path to Point Click Care — is locked. The Flags panel (Mark resolved
// included) and Accounts Summary are not merely locked here — they are
// HIDDEN entirely (owner item 3, 2026-08-13): a reviewer who cannot touch
// the fields a flag is about has nothing to do with a flag list or its
// rollup either. Import Control settles an ambiguous Company and nothing
// else (issue 3, 2026-08-11, `docs/workflow.md` §5, owner ruling 4). Its
// gate is every line item carrying a Company; see `allLineCompaniesSet`
// below.
//
// At AP/AR Approval the whole surface is live: flags are resolved here
// (auto on save when the condition holds; explicit via "Mark resolved"),
// amounts are finalized, and every line's expense type is coded. Its gate is
// the server's original three (unresolved flags, the amount reconcile, the
// expense-type check).
//
// "Save & Release" persists the edits and releases in one click at both
// steps; which gate applies is `canSaveAndRelease`'s two arms below.
//
// An invoice at any status besides the two above (or anything else the server
// refuses) renders with EVERY control locked — see `frozen`. A matching
// failure (question 4.11, 2026-08-12) is NOT this case any more: it lands at
// ordinary `Pending Import Control Decision` carrying a `MatchingFailed`
// flag, so it renders at Import Control's own (narrower) lock instead —
// `atImportControl`, not `frozen`.
//
// PORT NOTE — Save is now ONE request. `intakeSave` carries the header fields,
// the line upserts and removals, and (for Save & Release) the release itself.
// The server writes the correction rows, auto-resolves the flags whose condition
// now holds, recomputes the invoice's effective company and its routing, runs the
// release gates and performs the Import Control approve — as one transaction, so
// a release either happens completely or not at all. What used to be here and is
// now gone: the per-field PATCH sequence, the correction diff against a rebased
// baseline, the flag auto-resolution writes, the unresolved-count write, and the
// separate release decision. The arithmetic checks stay client-side for instant
// feedback; the server enforces them independently and is the authority.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Divider,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { AddRegular, CheckmarkCircleRegular, DeleteRegular, DismissRegular, LinkRegular } from '@fluentui/react-icons'
import type { InvoiceLineItem, InvoiceLineItemWrite, Flag, Invoice, IntakeSaveRequest } from '../lib/types'
import { checkAmounts, formatNumericDate, toNumber } from '../lib/format'
import {
  DecisionStep,
  DecisionOutcome,
  InvoiceStatus,
  PCS_COMPANY_ID,
  PCS_COMPANY_NAME,
  flagLabel,
  flagResolution,
  flagDescription,
} from '../lib/constants'
import {
  createConversation,
  createDecision,
  intakeSave,
  resolveFlag,
  searchExpenseTypes,
  searchCompanies,
  searchVendors,
} from '../lib/data'
import { errorMessage, isApiError } from '../api/errors'
import { useHalfLabels } from '../lib/hooks'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { AssociatedEntitiesEditor } from './AssociatedEntitiesEditor'
import { NotAnInvoiceDialog } from './NotAnInvoiceDialog'
import { DecisionDialog } from './DecisionDialog'
import { AccountsSummary } from './AccountsSummary'
import { FlagChip, DotPill } from './Badges'
import { TINT_PILLS } from '../lib/theme'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // Per-line action cell: entity-tag popover trigger + remove button.
  rowActions: { display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'flex-end' },
  // "Add line item" button row under the line items table.
  addRow: { display: 'flex', paddingTop: '4px' },
  // Save + Release pinned to the top of the scrolling modal body, always visible.
  //
  // WRAPS. The bar's own contents have a hard floor — Fluent's Button carries
  // `min-width: 96px`, and the bar holds four of them (Requires Manual Entry,
  // Not An Invoice, Save, Save & Release) plus the close button, so it cannot
  // render in less than that floor however narrow the modal gets. Without
  // `flexWrap` the overflow has nowhere to go: `s.scroll` in
  // `InvoiceDetailModal` sets `overflow-y: auto`, which per CSS computes
  // `overflow-x: visible` to `auto` as well, so the close button ends up off
  // the right edge behind a horizontal scrollbar. Wrapping puts the trailing
  // buttons on a second row instead — the modal is laid out for narrow widths
  // elsewhere (the 900px breakpoints below), so this is a real state, not a
  // hypothetical one.
  actionBar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    // Right-align the wrapped row, so the buttons that fall to line 2 stay under
    // the ones they belong with. No effect on line 1, where the status text grows
    // into all the free space there is.
    justifyContent: 'flex-end',
    padding: '12px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    // Blue rule under the modal's header band (mirrors the page's blue line).
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
  },
  // `flex: 1` is `1 1 0%`: a DEFINITE zero basis, so this item never forces a
  // wrap of its own however long the hint is (a `flex-basis: auto` text item
  // would contribute its whole max-content width to the line and push every
  // control onto row 2 at any width). The minWidth is a floor: rather than
  // squeeze the hint into a 60px column of single words, give up the line and
  // let the trailing buttons wrap. Only ever rendered blocked now (item 6,
  // 2026-08-12: the bar shows no prose at all when release is available), so
  // the color is fixed rather than switching between an ok/blocked pair.
  actionStatus: { flex: 1, minWidth: '200px', fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  // PDF + invoice header side-by-side on top; line items span full width below.
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  pdfWrap: { height: '440px', minHeight: 0, minWidth: 0, '@media (max-width: 900px)': { height: '300px' } },
  headerCol: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' },
  // AI extraction/matching notes — plain muted text under the summary fields.
  aiNotes: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    color: tokens.colorNeutralForeground3,
  },
  // Space between the extraction note and the matching note (item 1d,
  // 2026-08-13) — `aiNotes`' own `gap: 2px` is meant for a note wrapping
  // across lines, not for separating the two distinct notes from each other,
  // which used to run together with nothing between them.
  aiNoteGap: { marginTop: '12px' },
  // Flags render as a quiet list of chip + text rows (item 4, 2026-08-13) — a
  // flag is routine, not an error, so a full-width warning MessageBar per
  // flag overstated it.
  flagList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  flagNone: { color: tokens.colorNeutralForeground3 },
  flagRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  flagDetail: { color: tokens.colorNeutralForeground2, flex: 1, minWidth: '200px' },
  // Every editor fills its cell so the line item rows line up column-to-column.
  cellInput: { width: '100%', minWidth: 0 },
  // Fixed layout: sized utility/amount columns, Description takes the rest.
  // minWidth is the sum of the sized columns (710px) plus a floor for
  // Description, so the field that says what the line item IS never collapses when
  // the modal is narrow — below that the wrapper scrolls instead (QA Q17: at
  // 915px the Description input drew as a hairline and the header labels
  // overprinted each other). Header labels clip with an ellipsis rather than
  // spilling over the next column.
  lineItemsTable: {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: '940px',
    '& .fui-TableHeaderCell__button': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  // Let the line items table scroll horizontally rather than overlap when the modal
  // is narrow (the 7 columns have minimum widths that can exceed the width).
  // maxWidth pins the scroller to the modal so the table can't widen the dialog
  // itself (which clipped its own left edge — QA Q17).
  tableScroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  // Tint a line item's Account cell when a missing-account flag points at it.
  cellFlagged: { backgroundColor: tokens.colorPaletteRedBackground1, borderRadius: tokens.borderRadiusSmall },
  // "What is it?" cell. It held the picker plus the provenance "i" side by side
  // until issue 17 removed the popover; the flex row stays because the picker
  // still has to fill the cell and still has to be allowed to shrink inside a
  // fixed-layout table (`minWidth: 0` on both, or the EntityPicker's input sets
  // the column's floor).
  accountCell: { display: 'flex', alignItems: 'center', gap: '2px', minWidth: 0 },
  accountPicker: { flex: 1, minWidth: 0 },
  // The per-line entity editor now lives in a popover off the row's tag button
  // (2026-07-14) rather than an always-open row under every line item. Give the
  // surface a comfortable width so its 5-column rows don't crush.
  entityPop: { width: '540px', maxWidth: '82vw' },
  // Mounted inside the Dialog (see the mountNode note), so it shares a stacking
  // context with the sticky action bar (z-index 2) instead of sitting in the
  // body portal above everything. Lift it clear.
  entityPopSurface: { zIndex: 10 },
  // Hint shown for an unsaved line (no real line item id to hang entities on yet).
  entityHint: { display: 'block', padding: '6px 12px', color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // What is stored right now, shown after a concurrent edit collided with this one.
  conflict: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 12px',
    borderLeft: `3px solid ${tokens.colorPaletteRedBorderActive}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  conflictHead: { fontWeight: tokens.fontWeightSemibold },
})

interface HeaderState {
  externalinvoiceid: string
  date: string
  subtotal: string
  tax: string
  total: string
  currency: string
  vendorId?: string
  vendorName?: string
  companyId?: string
  companyName?: string
  /** The reviewer's REQUEST for manual Point Click Care entry
      (`manual_entry_requested`). The effective routing — the request or a
      cross-company split — is derived by the server and read-only here. Past
      the last staffed approval step a manual-entry invoice goes to Manual Entry
      instead of Export Control (app.md §9 Manual Entry). */
  manualEntry: boolean
}

interface LineItemState {
  lineItemId: string
  /** Concurrency counter of the stored line; absent on a line not saved yet. */
  rowVersion?: number
  sequence?: number | null
  description: string
  quantity: string
  price: string
  total: string
  /**
   * "What is it?" — the expense type. IMPORT CONTROL EDITS THIS HALF AND ONLY
   * THIS HALF. The cost center is not on the document and cannot be answered
   * here, so it is neither shown nor sent; the server refuses it either way.
   */
  expenseTypeCode?: string
  expenseTypeLabel?: string
  // NO expense-type PROVENANCE and no printed-code field. Both were read only by
  // the per-line "how was this expense type chosen" popover, which issue 17
  // removed, and neither is ever sent — `InvoiceLineItemWrite` has no source
  // column, so the server stamps provenance itself. Carrying write-only copies
  // of them here would read as though this form still tracked them.
  /** Per-line company (schema.md invoice_line_items). Untouched lines inherit the
      header company on save; a line the reviewer explicitly set (companyTouched,
      or stored differing from the invoice's) keeps its own — that's how one
      invoice splits across companies for the per-company PCC export. */
  companyId?: string
  companyName?: string
  companyTouched?: boolean
  /** True for a line the reviewer added manually and hasn't saved yet — created
      (not updated) on save, then flipped false once it has a real id. */
  isNew?: boolean
}

/** "A", "A and B", "A, B and C" — company names read as prose in the hint. */
/** The Company field's explanation, as a HOVER TOOLTIP and never as help text
    under the control (owner wording, 2026-08-10). Fixed copy: it states the
    rule, and the read-only field beside it already shows what the rule
    produced. */
const COMPANY_RULE =
  'The owner of the invoice is derived from the Company values assigned to each line item. ' +
  `If any line items disagree on Company, then the invoice is routed to ${PCS_COMPANY_NAME} for approval.`

function listCompanies(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

interface CompanyResolution {
  companyId?: string
  companyName?: string
  /** 'invoice' — the reviewer's own pick stands; 'lines' — every line points at
      one other company, which wins; 'mixed' — the lines disagree. */
  outcome: 'invoice' | 'lines' | 'mixed'
  /** Distinct company names the lines point at, for the hint text. */
  lineCompanyNames: string[]
}

/** The invoice's company follows its line items (user rule, 2026-07-31):
    unanimous lines set it, and lines that disagree bill it to PACE Consulting
    Solutions — which has staff to hand the approvals to — and route it to Manual
    Entry, because no single per-company export file fits it.

    The API applies this same rule and its answer is the one that gets stored;
    this copy exists so the reviewer can see where the invoice is going BEFORE
    releasing it, and so the Company field can say what set it. */
function resolveInvoiceCompany(
  invoiceCompanyId: string | undefined,
  invoiceCompanyName: string | undefined,
  lines: Array<{ companyId?: string; companyName?: string }>,
): CompanyResolution {
  const names = new Map<string, string>()
  for (const l of lines) {
    const id = l.companyId?.trim()
    if (!id) continue
    if (!names.has(id)) names.set(id, l.companyName?.trim() || id)
  }
  const ids = [...names.keys()]
  const lineCompanyNames = [...names.values()]
  const own: CompanyResolution = {
    companyId: invoiceCompanyId,
    companyName: invoiceCompanyName,
    outcome: 'invoice',
    lineCompanyNames,
  }
  if (ids.length === 0) return own
  if (ids.length === 1) {
    const id = ids[0]
    if (id === (invoiceCompanyId || '')) return own
    return { companyId: id, companyName: names.get(id), outcome: 'lines', lineCompanyNames }
  }
  return {
    companyId: PCS_COMPANY_ID,
    companyName: PCS_COMPANY_NAME,
    outcome: 'mixed',
    lineCompanyNames,
  }
}

/** Each line's EFFECTIVE company: a line the reviewer pinned uses its own, an
    untouched line follows the invoice's company. Always read from the header's
    OWN company — the value the reviewer picked and the save stores on the
    lines — never from the company the lines resolved to. Feeding the resolved
    company back in would move the untouched lines with it and flip the answer
    on the next pass. */
function effectiveLineCompanies(h: HeaderState, rows: LineItemState[]) {
  return rows.map((r) =>
    r.companyTouched
      ? { companyId: r.companyId, companyName: r.companyName }
      : { companyId: h.companyId, companyName: h.companyName },
  )
}

/** `companyId` is the invoice's company AFTER the lines have had their say
    (resolveInvoiceCompany) — lines that set the company also satisfy
    MissingCompany. */
function isAutoSatisfied(flag: string, h: HeaderState, lineItems: LineItemState[], companyId: string | undefined): boolean {
  switch (flag) {
    case 'MissingTotal':
      return h.total.trim() !== ''
    case 'MissingExternalInvoiceId':
      return h.externalinvoiceid.trim() !== ''
    case 'MissingVendor':
      return !!h.vendorId
    case 'MissingCompany':
      return !!companyId
    case 'MissingExpenseType':
      return lineItems.length > 0 && lineItems.every((c) => !!c.expenseTypeCode)
    case 'LineItemsToTotalMismatch': {
      // Same two-part test as the release gate (lines vs. SUBTOTAL since
      // 2026-08-11 — line items are pre-tax). No capped exemption: the line
      // items must add up to the subtotal for every invoice.
      const sum = lineItems.reduce((a, c) => a + toNumber(c.total), 0)
      return checkAmounts(sum, h.subtotal, h.tax, h.total).reconciles
    }
    default:
      return false
  }
}

// Format a numeric string to 2 decimal places for money-field display
// (e.g. "3918.6" -> "3918.60"). Blanks and non-numeric text pass through
// unchanged. toNumber() parses these back identically, so no data change.
function money2(v: string): string {
  if (v == null || v.trim() === '') return ''
  const n = Number(v)
  return Number.isNaN(n) ? v : n.toFixed(2)
}

interface Props {
  invoice: Invoice
  lineItems: InvoiceLineItem[]
  flags: Flag[]
  /** Which gate this mount runs: Import Control's Company-only gate, or
      AP/AR's full surface. Decides `atImportControl` below and nothing else —
      the endpoint and its authority are unchanged either way. */
  step: typeof DecisionStep.ImportControl | typeof DecisionStep.ApAr
  onChanged: () => void
  onReleased: () => void
  /** Called after the record is archived as not an invoice. */
  onArchived?: () => void
  /** Optional PDF preview, shown beside the header (Import Control). */
  pdf?: ReactNode
  /** Close the modal — rendered as an X in the header action bar. */
  onClose?: () => void
}

export function EditableInvoiceForm({ invoice, lineItems, flags, step, onChanged, onReleased, onArchived, pdf, onClose }: Props) {
  const s = useStyles()
  // Which gate this mount runs.
  const atImportControl = step === DecisionStep.ImportControl
  // Every control locked, including the per-line Company picker: the server
  // refuses any save or decision at any status other than the two this editor
  // covers (`out_of_order`), so a row at any other status must not offer a
  // button that 409s. A matching failure (question 4.11, 2026-08-12) is NOT
  // one of those other statuses any more — it lands at ordinary `Pending
  // Import Control Decision`, so this arm stays false for it and the row
  // renders at Import Control's own lock (`atImportControl` above) instead.
  const frozen = invoice.status !== InvoiceStatus.PendingImportControl && invoice.status !== InvoiceStatus.PendingApAr
  // Every header value and every line-item value except the per-line Company
  // picker (Import Control's own control). Used on the header grid and the
  // line-item table alike.
  const headerLocked = atImportControl || frozen
  // The Dialog element this form is rendered inside, used as the mount point for
  // the per-line entity popover.
  //
  // That popover holds real controls (the type dropdown, the source input, the
  // entity picker), and clicking any of them used to close it a beat later.
  // Verified in the browser, not guessed: focus left the control and landed on
  // the comment box 0.1–1s after the click, with tabster's
  // ModalizerAPI._restoreModalizerFocus on the stack. Two causes, both needed
  // fixing — a PopoverSurface portals to document.body, i.e. OUTSIDE the
  // Dialog's focus trap, and `trapFocus` gave it a second, nested trap. Either
  // way focus was sitting somewhere the Dialog's trap considered illegal, so
  // tabster hauled it back. Mounting the surface inside the Dialog and dropping
  // `trapFocus` leaves exactly one trap, with the popover inside it. Same
  // principle as the inlinePopup listboxes on Dropdown/Combobox. The mount point
  // is the Dialog surface, not the scrolling body, so the popover isn't clipped
  // by the scroll container.
  const rootRef = useRef<HTMLDivElement>(null)
  const [dialogEl, setDialogEl] = useState<HTMLElement | null>(null)
  useEffect(() => {
    // `role="dialog"` only — see `EntityPicker.tsx`'s matching comment on why
    // this does not also match `role="alertdialog"`.
    setDialogEl(rootRef.current?.closest('[role="dialog"]') as HTMLElement | null)
  }, [])
  const [header, setHeader] = useState<HeaderState>(() => ({
    externalinvoiceid: invoice.external_invoice_id ?? '',
    date: invoice.invoice_date ? invoice.invoice_date.slice(0, 10) : '',
    subtotal: invoice.subtotal != null ? money2(String(invoice.subtotal)) : '',
    tax: invoice.tax != null ? money2(String(invoice.tax)) : '',
    total: invoice.total != null ? money2(String(invoice.total)) : '',
    currency: invoice.currency ?? 'USD',
    vendorId: invoice.vendor_id || undefined,
    vendorName: invoice.vendor_name || undefined,
    companyId: invoice.company_id || undefined,
    companyName: invoice.company_name || undefined,
    manualEntry: invoice.manual_entry_requested ?? false,
  }))
  const [rows, setRows] = useState<LineItemState[]>(() =>
    lineItems.map((c) => ({
      lineItemId: c.id,
      rowVersion: c.row_version,
      sequence: c.line_number,
      description: c.description ?? '',
      quantity: c.quantity != null ? String(c.quantity) : '',
      price: c.unit_price != null ? money2(String(c.unit_price)) : '',
      total: c.total != null ? money2(String(c.total)) : '',
      expenseTypeCode: c.expense_type_code || undefined,
      companyId: c.company_id || undefined,
      companyName: c.company_name || undefined,
      // A stored company differing from the invoice's was set deliberately
      // (a cross-company split) — keep it pinned; matching-stamped copies of
      // the invoice company stay inherited and follow header edits.
      companyTouched: !!c.company_id && c.company_id !== (invoice.company_id || undefined),
    })),
  )
  // The stored invoice's concurrency counter. Sent with every save and replaced
  // from each response, so two people editing the same invoice can't silently
  // overwrite each other: the second save comes back 409 and we re-present what
  // is actually stored. (This replaced a rebased copy of the whole invoice + line
  // set, which the old client kept only to work out which fields to PATCH.)
  const [rowVersion, setRowVersion] = useState<number>(invoice.row_version)
  const [serverRow, setServerRow] = useState<Invoice | null>(null) // set on a version conflict
  const [removedIds, setRemovedIds] = useState<string[]>([]) // existing line item rows the reviewer removed
  const newIdRef = useRef(0) // counter for temp ids on unsaved added lines
  const [saving, setSaving] = useState(false)
  const [releasing, setReleasing] = useState(false)
  const [notInvoiceOpen, setNotInvoiceOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)
  // Requires Manual Entry (owner item 5, 2026-08-12) — see handleManualEntryFlip.
  const [manualDialogOpen, setManualDialogOpen] = useState(false)
  const [markingManual, setMarkingManual] = useState(false)
  const [dirty, setDirty] = useState(false) // unsaved header/line item edits
  const [resolving, setResolving] = useState(false) // a Mark-resolved write in flight
  const [error, setError] = useState<string | null>(null)
  // Edit counter, bumped by every mutator. A save persists the snapshot it
  // started from; anything typed while it was in flight leaves this higher than
  // the value captured at the start, so the save must NOT clear the dirty flag
  // and a release must not go out against the pre-edit values (QA Q11).
  const editSeq = useRef(0)

  const unresolvedFlags = useMemo(
    () => new Set(flags.filter((f) => !f.resolved_at).map((f) => f.flag_type)),
    [flags],
  )

  // Warm the shared empty-term results cache on open (EntityPicker keys it by
  // the search function), so the first Account picker opened on a many-line
  // invoice has its "top results" already in hand. That cache is a PAGE of
  // results, not the master, which is why the label lookup below is separate.
  useEffect(() => {
    void searchExpenseTypes('')
  }, [])

  // Code -> label for the expense types already on the loaded lines. See
  // `useHalfLabels`; the codes are what the wire carries.
  const labels = useHalfLabels()

  // Loaded line items keyed by id — lets the per-line entity editor (AE4) attach to
  // the real line item row. A line added and saved this session isn't in the loaded
  // set (line items reload is deferred), so the editor synthesizes a stub carrying
  // just the real id it needs.
  const lineItemsById = useMemo(() => new Map(lineItems.map((c) => [c.id, c])), [lineItems])

  // Arithmetic that gates release: the line items sum to the subtotal (they are
  // pre-tax), and the printed subtotal + tax = total. checkAmounts() in
  // lib/format.ts owns the rule and explains why.
  const lineItemsSum = useMemo(() => rows.reduce((a, c) => a + toNumber(c.total), 0), [rows])
  const { reconciles: amountsReconcile } = checkAmounts(
    lineItemsSum,
    header.subtotal,
    header.tax,
    header.total,
  )

  // Where this invoice is heading, derived live so the reviewer sees it before
  // releasing (resolveInvoiceCompany above). The server decides for real.
  //
  // The resolved company is NEVER written back into header.companyId: the line
  // list is built from the header's own company, so writing it back would move
  // every untouched line and re-resolve to a different answer each pass.
  const companyResolution = useMemo(
    () => resolveInvoiceCompany(header.companyId, header.companyName, effectiveLineCompanies(header, rows)),
    [rows, header],
  )
  // The company the invoice is released and stored under.
  const companyId = companyResolution.companyId
  const companyName = companyResolution.companyName
  // The Company field is derived and read-only (issue 5). "Pending" keys on the
  // line companies being EMPTY — never on `outcome === 'invoice'`, which ALSO
  // fires when every line agrees with the company already stored, the opposite
  // of pending. Empty means either no line items at all or no line carrying a
  // company. Pending ALWAYS implies release is blocked (not the converse — plenty
  // of other things block it), and it does so through TWO different gates, so do
  // not simplify either into the other: with no lines at all `companyId` is the
  // stored one, truthy whenever the invoice has one; with lines but no company
  // anywhere, `companyId` is undefined. `canSaveAndRelease` below has two arms
  // now (issue: apar-ic) — at Import Control it gates on `allLineCompaniesSet`
  // (every line's EFFECTIVE company, which also covers the no-lines-at-all
  // case), and at AP/AR it gates on `!!companyId` plus the three server-side
  // checks.
  const companyPending = companyResolution.lineCompanyNames.length === 0
  // `resolveInvoiceCompany` falls back to the id when a line carries no name, so
  // this does the same rather than rendering an empty box.
  const companyDisplay = companyPending ? 'Pending' : companyName || companyId || 'Pending'
  // What the save must store: the reviewer's own Manual Entry pick, or a
  // mixed-company invoice, which has no CSV path.
  const manualEntryEffective = header.manualEntry || companyResolution.outcome === 'mixed'
  // Locked at Import Control (its own screen, always — Path to Point Click
  // Care is AP/AR's to set), when frozen, or when the lines disagree on
  // Company (no single CSV path fits a mixed-company invoice, at either step).
  const pathLocked = atImportControl || frozen || companyResolution.outcome === 'mixed'

  // Flags that would still block after a save: explicit ones not yet marked
  // resolved, and auto ones whose condition doesn't hold yet. Auto flags whose
  // condition holds don't block — Save & Release resolves them on its save.
  const blockingFlags = flags.filter(
    (f) =>
      !f.resolved_at &&
      !(flagResolution(f.flag_type) === 'auto' && f.flag_type && isAutoSatisfied(f.flag_type, header, rows, companyId)),
  ).length

  // Every line needs an EXPENSE TYPE (QA Q12). NOT a cost center: that half is
  // settled at Department Approval or later by someone who knows which
  // discipline the spend was for, and gating release on it here would make a
  // rubber stamp mandatory on the 73% of the chart whose expense types admit
  // more than one cost center. This mirrors the server's third release gate
  // exactly — `line_item_without_expense_type`.
  //
  // The `MissingExpenseType` flag only exists when Matching raised one, so a
  // line the reviewer ADDS here carries no flag and used to sail through — and
  // then aborted the whole Export Control batch, four steps later.
  const linesWithoutExpenseType = rows.filter((r) => !r.expenseTypeCode)

  // Import Control's gate (owner ruling 4): every line item's EFFECTIVE
  // Company populated — an untouched row counts the header's own company
  // (`effectiveLineCompanies`'s rule), and an invoice with no lines at all
  // cannot satisfy "every line", so it stays blocked too. Stricter than the
  // server's own release check (effective company non-null over the WHOLE
  // invoice, `invoices.ts:288`) — the owner's instruction, not a bug: a
  // four-line invoice with only one line Companied is releasable by the API
  // and withheld here.
  const allLineCompaniesSet = rows.length > 0 && rows.every((r) => !!(r.companyTouched ? r.companyId : header.companyId))

  const canSaveAndRelease = !frozen && (atImportControl ? allLineCompaniesSet : !!companyId && rows.length > 0 && blockingFlags === 0 && amountsReconcile && linesWithoutExpenseType.length === 0)

  // Field-level highlight: which fields are driving an unresolved flag. Import
  // Control locks the fields these describe, so red on them there is noise.
  // NOTE (owner item 3, 2026-08-13): Import Control no longer renders the Flags
  // panel at all, so it now carries NO flag surface — no list, no chip, no red
  // field. That is deliberate: the step's only job is settling the per-line
  // Company, and every flag here is AP/AR's to resolve. Do not read the missing
  // panel as an oversight and re-add a highlight. Company keeps its error
  // state regardless:
  // Company is what Import Control is for. For the auto-clearing flags, drop
  // the highlight the moment the value is fixed (before save) so a corrected
  // field doesn't keep showing red; low-confidence flags need an explicit
  // resolve, so they stay highlighted until then.
  const showFieldErrors = !atImportControl
  const has = (f: string) => unresolvedFlags.has(f)
  const vendorErr = showFieldErrors && ((has('MissingVendor') && !header.vendorId) || has('VendorMatchLowConfidence'))
  const companyErr = (has('MissingCompany') && !companyId) || has('CompanyMatchLowConfidence')
  const extIdErr = showFieldErrors && has('MissingExternalInvoiceId') && !header.externalinvoiceid.trim()
  const totalErr = showFieldErrors && ((has('MissingTotal') && !header.total.trim()) || (has('LineItemsToTotalMismatch') && !amountsReconcile))
  const currencyErr = showFieldErrors && has('NonUSDCurrency') && header.currency.trim().toUpperCase() !== 'USD'
  // No MissingAccount highlight variable: the Account cell is tinted from the
  // line itself now (QA Q12), which covers flagged and reviewer-added lines alike.

  // Every mutator goes through this: flags the form dirty and bumps the edit
  // counter a save in flight compares itself against (QA Q11).
  function touched() {
    editSeq.current += 1
    setDirty(true)
  }

  function setH<K extends keyof HeaderState>(key: K, value: HeaderState[K]) {
    touched()
    setHeader((prev) => ({ ...prev, [key]: value }))
  }

  function patchHeader(patch: Partial<HeaderState>) {
    touched()
    setHeader((prev) => ({ ...prev, ...patch }))
  }

  function setRow(id: string, patch: Partial<LineItemState>) {
    touched()
    setRows((prev) => prev.map((r) => (r.lineItemId === id ? { ...r, ...patch } : r)))
  }

  function addRow() {
    touched()
    newIdRef.current += 1
    setRows((prev) => {
      const maxSeq = prev.reduce((m, r) => Math.max(m, r.sequence ?? 0), 0)
      return [
        ...prev,
        {
          lineItemId: `new-${newIdRef.current}`,
          sequence: maxSeq + 1,
          description: '',
          quantity: '',
          price: '',
          total: '',
          isNew: true,
        },
      ]
    })
  }

  function removeRow(id: string) {
    touched()
    const row = rows.find((r) => r.lineItemId === id)
    if (row && !row.isNew) setRemovedIds((ids) => [...ids, id]) // deactivate existing rows on save
    setRows((prev) => prev.filter((r) => r.lineItemId !== id))
  }

  // Save. One request carries the header, the line upserts, the removals and —
  // when `release` is set — the release itself, applied as a single transaction.
  //
  // Returns whether the release went through AND whether the reviewer edited
  // anything while the request was in flight (QA Q11). The save persists the
  // snapshot it started from — that is unavoidable, the round trip takes as long
  // as it takes — so what matters is that the form does not then LIE about it: it
  // stays dirty, Save stays enabled, and a release is refused rather than sending
  // the pre-edit values on to the approvers.
  //
  // `headerOverride` is for a caller that must save a value it just decided
  // rather than one already sitting in `header` state: React state updates are
  // not synchronous, so a setter call right before `persistAll` cannot be
  // trusted to have landed yet (owner item 5, 2026-08-12 — Requires Manual
  // Entry saves the mark in the same click that collects its reason).
  async function persistAll(release: boolean, headerOverride?: Partial<HeaderState>): Promise<{ released: boolean; raced: boolean }> {
    const seqAtStart = editSeq.current
    // Freeze exactly what this save writes, so every part of it reads one
    // consistent set of values.
    const savedHeader = headerOverride ? { ...header, ...headerOverride } : header
    const savedRows = rows
    const savedRemoved = removedIds
    // A blank field CLEARS the stored column: the request is PATCH-shaped, where
    // an absent key means "leave alone" and null means "clear". Sending null for
    // a field that was already empty is a no-op, so no before/after bookkeeping
    // is needed to tell the two apart.
    const textOrNull = (v: string): string | null => (v.trim() !== '' ? v.trim() : null)
    const numOrNull = (v: string): number | null => (v.trim() !== '' ? toNumber(v) : null)

    // An edit typed while the request is in flight is not in what was stored, so
    // a release now would send the pre-edit values on (QA Q11). Ask for the
    // release only if nothing has changed since this snapshot was taken.
    const raced = () => editSeq.current !== seqAtStart

    const upsert: InvoiceLineItemWrite[] = savedRows.map((r) => ({
      // No id = create. With an id the line's own concurrency counter rides
      // along, so an edit to a line someone else just changed is refused rather
      // than silently winning.
      ...(r.isNew ? {} : { id: r.lineItemId, row_version: r.rowVersion }),
      line_number: r.sequence ?? null,
      description: textOrNull(r.description),
      // THE EXPENSE TYPE ONLY. `cost_center_code` is deliberately not sent:
      // under the absent-vs-null convention sending it null would CLEAR a
      // `ChartImplied` cost center on every Import Control save.
      expense_type_code: r.expenseTypeCode ?? null,
      // Materialize the line's company: explicitly-set lines keep their own,
      // everything else inherits the header's OWN company (this is what splits
      // the export into one file per company — export.md). Not the company the
      // lines resolved to: an untouched line's cost belongs to the company the
      // reviewer billed the invoice to, and storing the resolved value would
      // re-point every line each time the answer moved.
      company_id: (r.companyTouched ? r.companyId : savedHeader.companyId) ?? null,
      company_name: (r.companyTouched ? r.companyName : savedHeader.companyName) ?? null,
      quantity: numOrNull(r.quantity),
      unit_price: numOrNull(r.price),
      total: numOrNull(r.total),
    }))

    const body: IntakeSaveRequest = {
      row_version: rowVersion,
      invoice: {
        external_invoice_id: textOrNull(savedHeader.externalinvoiceid),
        invoice_date: textOrNull(savedHeader.date),
        subtotal: numOrNull(savedHeader.subtotal),
        tax: numOrNull(savedHeader.tax),
        total: numOrNull(savedHeader.total),
        currency: textOrNull(savedHeader.currency),
        vendor_id: savedHeader.vendorId ?? null,
        vendor_name: savedHeader.vendorName ?? null,
        // The company already stored on the invoice, sent back unchanged — the
        // reviewer has no way to alter it here since issue 5 made the field
        // read-only. It still matters: it is what an UNTOUCHED line inherits,
        // so it is an input to the derivation, not an output of it. The server
        // re-derives the effective company from the lines, exactly as the hint
        // above predicts.
        company_id: savedHeader.companyId ?? null,
        company_name: savedHeader.companyName ?? null,
        // The REQUEST only. A cross-company split adds itself to this
        // server-side; `manual_entry` is derived and never written from here.
        manual_entry_requested: savedHeader.manualEntry,
      },
      line_items: { upsert, remove: savedRemoved },
      release: release && !raced(),
    }

    const result = await intakeSave(invoice.id, body)
    const releaseRequested = body.release === true
    // Reseed from what the server stored — the response is the record. Skip the
    // reseed when the reviewer typed during the round trip: their unsaved values
    // must survive, and the form stays dirty to say so.
    const stillClean = !raced()
    setRowVersion(result.invoice.row_version)
    setServerRow(null)
    if (stillClean) {
      const saved = result.invoice
      setHeader({
        externalinvoiceid: saved.external_invoice_id ?? '',
        date: saved.invoice_date ? saved.invoice_date.slice(0, 10) : '',
        subtotal: saved.subtotal != null ? money2(String(saved.subtotal)) : '',
        tax: saved.tax != null ? money2(String(saved.tax)) : '',
        total: saved.total != null ? money2(String(saved.total)) : '',
        currency: saved.currency ?? 'USD',
        vendorId: saved.vendor_id || undefined,
        vendorName: saved.vendor_name || undefined,
        // Keep the reviewer's own pick, not the resolved company: the lines are
        // built from it, so adopting the resolved value would move every
        // untouched line on the next save.
        companyId: savedHeader.companyId,
        companyName: savedHeader.companyName,
        manualEntry: saved.manual_entry_requested ?? false,
      })
      // Adopt the stored lines: a created line arrives with its real id, and
      // every line with a fresh concurrency counter for the next save.
      if (result.line_items) setRows(result.line_items.map(lineItemToRow))
      setRemovedIds([])
      setDirty(false)
    } else {
      // Keep every value the reviewer typed, but take the identity the server
      // just assigned: a line created by this save now HAS an id, and re-sending
      // it as new would create a second copy of it.
      if (result.line_items) adoptLineIdentity(result.line_items)
      // The removals this save processed are done; anything deleted since is not.
      setRemovedIds((prev) => prev.filter((id) => !savedRemoved.includes(id)))
    }
    return { released: releaseRequested && !!result.released_to, raced: !stillClean }
  }

  /** Take the stored lines' ids and concurrency counters without touching any
      value on screen. Used when a save landed but the reviewer had typed since:
      their text stays, while a line the save created stops being "new". Created
      lines are paired by line number — they come back with the number they were
      sent with — and otherwise in the order they were sent. */
  function adoptLineIdentity(stored: InvoiceLineItem[]) {
    setRows((prev) => {
      const known = new Set(prev.filter((r) => !r.isNew).map((r) => r.lineItemId))
      const pool = stored.filter((c) => !known.has(c.id))
      const take = (sequence: number | null | undefined): InvoiceLineItem | undefined => {
        let at = sequence == null ? -1 : pool.findIndex((c) => c.line_number === sequence)
        if (at < 0) at = 0
        return pool.splice(at, 1)[0]
      }
      return prev.map((r) => {
        if (r.isNew) {
          const c = take(r.sequence)
          return c ? { ...r, lineItemId: c.id, rowVersion: c.row_version, isNew: false } : r
        }
        const c = stored.find((x) => x.id === r.lineItemId)
        return c ? { ...r, rowVersion: c.row_version } : r
      })
    })
  }

  /** Turn a stored line back into editable row state (seed + post-save reseed). */
  function lineItemToRow(c: InvoiceLineItem): LineItemState {
    return {
      lineItemId: c.id,
      rowVersion: c.row_version,
      sequence: c.line_number,
      description: c.description ?? '',
      quantity: c.quantity != null ? String(c.quantity) : '',
      price: c.unit_price != null ? money2(String(c.unit_price)) : '',
      total: c.total != null ? money2(String(c.total)) : '',
      expenseTypeCode: c.expense_type_code || undefined,
      companyId: c.company_id || undefined,
      companyName: c.company_name || undefined,
      companyTouched: !!c.company_id && c.company_id !== (header.companyId || undefined),
    }
  }

  /** Turn a refusal into something the reviewer can act on. The API's message is
      human text, so most cases pass it through; the two release gates and a
      concurrent edit each get wording that says what to do next. */
  function reportSaveError(e: unknown, verb: 'Save' | 'Release') {
    if (isApiError(e)) {
      if (e.code === 'unresolved_flags') {
        const types = (e.details?.flag_types as string[] | undefined) ?? []
        setError(
          types.length > 0
            ? `Not released — ${types.length === 1 ? 'this flag is' : 'these flags are'} still unresolved: ${types
                .map(flagLabel)
                .join(', ')}. Nothing was saved.`
            : `${e.message} Nothing was saved.`,
        )
        return
      }
      if (e.code === 'amounts_do_not_reconcile') {
        setError(`Not released — ${e.message} Nothing was saved.`)
        return
      }
      if (e.code === 'version_conflict') {
        // Someone else changed this invoice since it was opened. Show what is
        // actually stored rather than retrying blind over their edit.
        const current = e.details?.current as Invoice | undefined
        if (current) {
          setServerRow(current)
          setRowVersion(current.row_version)
        }
        setError(
          'Someone else changed this invoice while you were editing it, so nothing was saved. Their version is shown below the message; re-apply anything still needed and save again.',
        )
        return
      }
      setError(`${verb} failed: ${e.message}`)
      return
    }
    setError(`${verb} failed: ${errorMessage(e)}`)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const { raced } = await persistAll(false)
      if (raced) {
        setError('Saved — but you changed the invoice while it was saving, so those later edits are not saved yet. Save again.')
      }
      onChanged()
    } catch (e) {
      reportSaveError(e, 'Save')
    } finally {
      setSaving(false)
    }
  }

  async function handleResolveExplicit(flag: Flag) {
    if (resolving) return // guard against a double-click racing two writes
    setResolving(true)
    setError(null)
    try {
      await resolveFlag(invoice.id, flag.id)
      onChanged()
    } catch (e) {
      setError(`Could not mark resolved: ${errorMessage(e)}`)
    } finally {
      setResolving(false)
    }
  }

  // Save and release in one click — and in one request: the server saves, clears
  // the flags whose condition now holds, checks the release gates and writes the
  // Import Control approval, or refuses and stores nothing.
  async function handleSaveAndRelease() {
    setReleasing(true)
    setError(null)
    try {
      const { released, raced } = await persistAll(true)
      if (raced) {
        setError('Not released — you changed the invoice while it was saving. Those edits are not stored yet: save again, then release.')
        onChanged()
        return
      }
      if (!released) {
        setError('Saved, but not released. Check the flags and the amounts, then release again.')
        onChanged()
        return
      }
      onReleased()
    } catch (e) {
      reportSaveError(e, 'Release')
    } finally {
      setReleasing(false)
    }
  }

  // Archive a record that isn't a payable invoice (statement, cover/routing
  // page, junk scan). One decision; the API checks the CALLER'S step authority
  // (Import Control's role, or AP/AR's company-scoped assignment) and moves the
  // invoice to the terminal 'Archived Not An Invoice'. No category — the
  // dialog collects only the free-text reason (owner ruling: sufficient on its
  // own, and this outcome does not share Deny/Archive's category vocabulary).
  async function handleNotAnInvoice(reason: string) {
    setArchiving(true)
    setError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step,
        decision: DecisionOutcome.ArchivedNotAnInvoice,
        reason,
      })
      setNotInvoiceOpen(false)
      ;(onArchived ?? onReleased)()
    } catch (e) {
      setError(`Archive failed: ${errorMessage(e)}`)
    } finally {
      setArchiving(false)
    }
  }

  // "Requires Manual Entry" (owner item 5, 2026-08-12). Was the "Path to Point
  // Click Care" Dropdown, which set `manualEntry` in form state and left it to
  // Save. Same column, same endpoint (`manual_entry_requested` via
  // intakeSave), same derivation, same reversibility — but the write happens
  // HERE, in the gesture that collects the reason, because a reason that
  // outlives its own save by a click is a reason nobody sees. The reason has
  // no column of its own — `corrections` has no reason column and this is not
  // a decision — so it lands on the invoice's comment thread, the only
  // free-text home the shipped contract has.
  async function handleManualEntryFlip(next: boolean, reason: string) {
    setMarkingManual(true)
    setError(null)
    try {
      const { raced } = await persistAll(false, { manualEntry: next })
      // The reseed is skipped when the reviewer typed mid-flight, so carry the
      // stored mark into form state by hand — the save DID write it.
      if (raced) patchHeader({ manualEntry: next })
      setManualDialogOpen(false)
      const notes: string[] = []
      if (raced) notes.push('You changed the invoice while it was saving, so those later edits are not stored yet. Save again.')
      try {
        await createConversation(
          invoice.id,
          `${next ? 'Requires Manual Entry' : 'Sent back to Export / Import'} — ${reason}`,
          [],
        )
      } catch (e) {
        notes.push(`The reason could not be posted to the comment thread (${errorMessage(e)}). Please add it as a comment below.`)
      }
      if (notes.length > 0) setError(notes.join(' '))
      // AFTER the comment: the modal refetches the thread on this callback.
      onChanged()
    } catch (e) {
      // Nothing was stored, so the mark is not applied and the dialog stays open.
      reportSaveError(e, 'Save')
    } finally {
      setMarkingManual(false)
    }
  }

  // Why Save & Release is blocked, for the ONE line of prose the action bar
  // still shows. Frozen wins over everything: a status this editor does not
  // cover offers no path forward at all. Otherwise the only case worth a
  // sentence is a missing company — the one gate that has no other visible
  // trace on this form. Everything else `canSaveAndRelease` checks at AP/AR
  // (unresolved flags, the amounts, an uncoded line) is already visible lower
  // on the same screen — the Flags panel, the reconcile check, the tinted
  // cell — so repeating it here said nothing new and restated the Flags panel
  // and the line items below it (owner item 6, 2026-08-12). The bar used to
  // also carry "Ready for release to AP/AR…" for the unblocked case.
  // `undefined` means the bar shows no prose at all.
  // At AP/AR, `canSaveAndRelease`'s own arm also requires `rows.length > 0`
  // (every line item removed, or an extraction that returned none) — a gate
  // with no other visible trace on this form, same as a missing company. Fold
  // it into `companyMissing` so it gets the same sentence rather than greying
  // the button with nothing on screen to explain why (review finding,
  // 2026-08-12).
  const companyMissing = atImportControl ? !allLineCompaniesSet : !companyId || rows.length === 0
  const releaseBlockedReason = frozen
    ? `This invoice's status is ${invoice.status}, so it cannot be edited or released here.`
    : companyMissing
      ? rows.length === 0
        ? 'Add at least one line item and point it at a company to release.'
        : 'Point at least one line item at a company to release.'
      : undefined

  // Tooltip on the Requires Manual Entry button. Three branches, and the
  // mixed-company one wins whenever it applies even though `pathLocked` is
  // ALSO true then (a mixed-company invoice is locked for that reason among
  // others) — it is the more useful thing to tell the reviewer. The other two
  // branches are step-locked (Import Control / frozen) and unlocked, where the
  // wording depends on whether the mark is already set.
  const manualEntryTitle =
    companyResolution.outcome === 'mixed'
      ? `Set by the line items: lines are pointed at ${listCompanies(companyResolution.lineCompanyNames)}, so this invoice is keyed into Point Click Care by hand.`
      : pathLocked
        ? 'Import Control settles the Company only. How this invoice reaches Point Click Care is set at AP/AR Approval.'
        : header.manualEntry
          ? 'Marked as requiring manual entry — it will be keyed into Point Click Care by hand instead of going out in the CSV export. Click to send it back to Export / Import.'
          : 'Mark this invoice as one that has to be keyed into Point Click Care by hand instead of going out in the CSV export.'

  return (
    <div className={s.root} ref={rootRef}>
      {error && (
        <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      )}

      {/* What is actually stored, after someone else's edit collided with this
          one. Shown rather than merged: only the reviewer can tell which of the
          two values is right. */}
      {serverRow && (
        <div className={s.conflict}>
          <Caption1 className={s.conflictHead}>Currently stored</Caption1>
          <Caption1>Company: {serverRow.company_name ?? '—'} · Vendor: {serverRow.vendor_name ?? '—'}</Caption1>
          <Caption1>
            External ID: {serverRow.external_invoice_id ?? '—'} · Invoice date: {formatNumericDate(serverRow.invoice_date)}
          </Caption1>
          <Caption1>
            Subtotal {serverRow.subtotal ?? '—'} · Tax {serverRow.tax ?? '—'} · Total {serverRow.total ?? '—'}
          </Caption1>
        </div>
      )}

      <div className={s.actionBar}>
        {/* Subtitle2 so the status reads at the same weight as the section
            headings. Rendered only when release is blocked (item 6,
            2026-08-12) — see `releaseBlockedReason`. */}
        {releaseBlockedReason && <Subtitle2 className={s.actionStatus}>{releaseBlockedReason}</Subtitle2>}
        {/* Requires Manual Entry (owner item 5, 2026-08-12) — replaced the
            "Path to Point Click Care" Dropdown that used to sit here. Same
            column (`manual_entry_requested`), same derivation
            (`manualEntryEffective`), same lock rule (`pathLocked`): Import
            Control settles the Company only, so the button is locked here
            always; AP/AR sets it. A mixed-company invoice is Manual Entry by
            rule regardless of step — `manualEntryTitle` above says so.

            `disabledFocusable` rather than `disabled` when locked: Fluent
            will not fire a Tooltip on a plain disabled child, and the lock
            explanation is the whole point of the hover (verified in the
            installed typings, @fluentui/react-button
            ButtonProps.disabledFocusable). */}
        {/* Hidden at Import Control (owner item 3, 2026-08-13): that step
            settles the Company only, and the button is locked there anyway
            (`pathLocked`) — nothing on this screen is left for it to explain.
            AP/AR keeps it. */}
        {!atImportControl && (
          <Tooltip content={manualEntryTitle} relationship="description" withArrow>
            <Button
              appearance="secondary"
              icon={manualEntryEffective ? <CheckmarkCircleRegular /> : undefined}
              disabled={!pathLocked && (saving || releasing || archiving || markingManual)}
              disabledFocusable={pathLocked}
              onClick={() => { if (!pathLocked) setManualDialogOpen(true) }}
            >
              Requires Manual Entry
            </Button>
          </Tooltip>
        )}
        <Button appearance="secondary" onClick={() => setNotInvoiceOpen(true)} disabled={saving || releasing || archiving || markingManual || frozen}>
          Not An Invoice
        </Button>
        {/* Divider between "Not An Invoice" and the Save pair (item 14,
            2026-08-13) — `vertical` plus a fixed height rather than `stretch`,
            since `s.actionBar`'s `alignItems: center` gives every child its
            own content height and there is no row height to stretch to.
            `flexGrow: 0` IS LOAD-BEARING: Fluent's Divider base style sets
            `flex-grow: 1` and the `vertical` variant does not reset it, so
            without this the rule absorbs all the free space on the bar and
            parks itself in the middle of the gap instead of beside the
            buttons. `flexShrink: 0` keeps it from collapsing when the bar
            wraps. */}
        <Divider vertical style={{ height: '24px', flexGrow: 0, flexShrink: 0 }} />
        <Button appearance="secondary" onClick={handleSave} disabled={saving || releasing || archiving || markingManual || !dirty || frozen}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button appearance="primary" onClick={handleSaveAndRelease} disabled={!canSaveAndRelease || saving || releasing || archiving || markingManual}>
          {releasing ? 'Releasing…' : 'Save & Release'}
        </Button>
        {onClose && (
          <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={onClose} />
        )}
      </div>

      <NotAnInvoiceDialog
        open={notInvoiceOpen}
        submitting={archiving}
        onArchive={handleNotAnInvoice}
        onCancel={() => setNotInvoiceOpen(false)}
      />

      {/* Two DecisionDialogs, one per direction — a routing flip either way is
          worth explaining, and requiring the reason on the way back is what
          lets someone restate why on an invoice marked in an earlier session
          (mark it back, then re-mark it). Keyed on `header.manualEntry` (the
          REQUEST), never on `manualEntryEffective`: on a mixed-company invoice
          the effective mark is true by rule while the request may be false,
          and that invoice's button is locked anyway, so neither dialog opens. */}
      <DecisionDialog
        open={manualDialogOpen && !header.manualEntry}
        title="Requires Manual Entry"
        body="This invoice goes through the approval chain as normal, then is keyed into Point Click Care by hand at the end instead of going out in the CSV export."
        requireReason
        reasonLabel="Reason"
        reasonPlaceholder="Required — recorded on this invoice's comment thread."
        confirmLabel="Requires Manual Entry"
        note="Confirming saves the invoice as well as the mark."
        submitting={markingManual}
        onConfirm={(reason) => void handleManualEntryFlip(true, reason)}
        onCancel={() => setManualDialogOpen(false)}
      />
      <DecisionDialog
        open={manualDialogOpen && header.manualEntry}
        title="Requires Manual Entry"
        body="This invoice is marked as requiring manual entry. Sending it back means it leaves in the CSV export with the rest of its company's invoices."
        requireReason
        reasonLabel="Reason"
        reasonPlaceholder="Required — recorded on this invoice's comment thread."
        confirmLabel="Send back to Export / Import"
        note="Confirming saves the invoice as well as the change."
        submitting={markingManual}
        onConfirm={(reason) => void handleManualEntryFlip(false, reason)}
        onCancel={() => setManualDialogOpen(false)}
      />

      {/* Flags hidden entirely at Import Control (owner item 3, 2026-08-13):
          that step settles the Company only, and the fields these flags are
          about are locked on this screen — a reviewer who cannot inspect them
          has nothing to do with a flag list either. Flag resolution belongs
          to AP/AR Approval, which is the only step that ever mounts this
          section now (`docs/decisions.md`). */}
      {!atImportControl && (
        <>
          {/* Count the flags that still block (explicit not-yet-resolved + auto
              whose condition doesn't hold), so this heading agrees with the
              release banner instead of showing "N unresolved" while the banner
              says "all resolved". */}
          <Subtitle2>Flags ({blockingFlags} unresolved)</Subtitle2>
          {flags.length === 0 ? (
            <Body1 className={s.flagNone}>No flags on this invoice.</Body1>
          ) : (
            <div className={s.flagList}>
              {flags.map((f) => {
                const persistedResolved = !!f.resolved_at
                // An auto flag whose condition already holds will clear on the next
                // save — render it as resolved so the list matches the release banner.
                const autoWillClear =
                  !persistedResolved &&
                  flagResolution(f.flag_type) === 'auto' &&
                  !!f.flag_type &&
                  isAutoSatisfied(f.flag_type, header, rows, companyId)
                const resolved = persistedResolved || autoWillClear
                const explicit = flagResolution(f.flag_type) === 'explicit'
                return (
                  // Quiet presentation (item 4, 2026-08-13): a flag is routine,
                  // not an error, so it no longer renders as a full-width
                  // warning MessageBar. A small tinted chip carries the
                  // resolved/unresolved distinction — green once resolved,
                  // the house `FlagChip` tint (warning for auto, danger for
                  // explicit) until then — beside the same label, help text
                  // and resolve control as before.
                  <div key={f.id} className={s.flagRow}>
                    {resolved ? (
                      <DotPill pill={TINT_PILLS.green}>{flagLabel(f.flag_type)}</DotPill>
                    ) : (
                      <FlagChip flag={f.flag_type} />
                    )}
                    <Caption1 className={s.flagDetail}>
                      {persistedResolved
                        ? `Resolved by ${f.resolved_by_name ?? 'user'}.`
                        : autoWillClear
                        ? 'Ready — clears on save.'
                        : // Show the human-readable help text, then append the flow's
                          // detail only when it adds context (a matched company,
                          // duplicate invoice #, GL code…). A bare numeric detail is an
                          // internal count, not user-facing — appending "— 1" only
                          // confused reviewers (user note 2026-07-14). Earlier this
                          // field could even replace the whole description; now it's
                          // strictly supplementary.
                          `${flagDescription(f.flag_type)}${f.detail && /\D/.test(f.detail) ? ` — ${f.detail}` : ''}`}
                    </Caption1>
                    {!resolved && explicit && !frozen && (
                      <Button size="small" disabled={resolving} onClick={() => handleResolveExplicit(f)}>
                        Mark resolved
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Plain rule where the Flags section's own blue band used to close it
          (item 4, 2026-08-13): the band was a deliberate pair with the action
          bar's rule above (C6), but Flags itself is delimited the same way
          every other section here is — a plain Divider, same as the one
          leading into Line Items Summary and into Accounts Summary below. */}
      <Divider />
      <div className={s.topRow} style={pdf ? undefined : { display: 'block' }}>
        {pdf && <div className={s.pdfWrap}>{pdf}</div>}
        <div className={s.headerCol}>
          <Subtitle2>Invoice Summary</Subtitle2>
          <div className={s.grid}>
            {/* The invoice's company is DERIVED from the line items and is not
                picked here (user rule 2026-07-31, `resolveInvoiceCompany`
                above): unanimous lines set it, and lines that disagree bill it
                to PACE Consulting Solutions and route it to Manual Entry. It
                used to be a picker that disabled itself whenever the lines were
                driving, which left one narrow state — lines silent, or all
                agreeing with the stored company — where typing here changed the
                answer. Read-only removes that state: the way to change the
                company is always to repoint a line, and the per-line Company
                picker below is where that happens. Reads "Pending" until some
                line carries one (owner, 2026-08-10); release stays blocked for
                as long as it does, by whichever of `canSaveAndRelease`'s two
                gates applies — see the note beside `companyPending` above.

                NO `hint`. The explanation is a HOVER TOOLTIP and must stay one
                (owner, 2026-08-10): `Field`'s hint renders as a permanent block
                of help text under the control, which is what was asked to go.
                The copy is fixed rather than per-state for the same reason —
                it states the rule, and the field itself already shows the
                answer that rule produced. */}
            <Tooltip content={COMPANY_RULE} relationship="description" withArrow>
              <Field label="Company" validationState={companyErr ? 'error' : 'none'}>
                <Input value={companyDisplay} readOnly appearance="filled-darker" />
              </Field>
            </Tooltip>
            <Field label="Vendor" validationState={vendorErr ? 'error' : 'none'}>
              <EntityPicker
                disabled={headerLocked}
                value={{ id: header.vendorId, name: header.vendorName }}
                placeholder="Search vendors…"
                search={(t) =>
                  searchVendors(t).then<EntityOption[]>((vs) =>
                    vs.map((v) => ({
                      id: v.vendor_id ?? '',
                      primary: v.vendor_name ?? '(unnamed)',
                      secondary: [v.city, v.state_or_province].filter(Boolean).join(', '),
                    })),
                  )
                }
                onSelect={(o) => patchHeader({ vendorId: o.id, vendorName: o.primary })}
              />
            </Field>
            <Field label="Internal ID">
              <Input value={invoice.id ?? '—'} readOnly appearance="filled-darker" />
            </Field>
            <Field label="External ID" validationState={extIdErr ? 'error' : 'none'}>
              <Input
                value={header.externalinvoiceid}
                onChange={(_, d) => setH('externalinvoiceid', d.value)}
                readOnly={headerLocked}
                appearance={headerLocked ? 'filled-darker' : undefined}
              />
            </Field>
            <Field label="Invoice Date">
              {/* `type` swaps to `text` while locked — a readOnly `type="date"`
                  still opens the Chromium calendar picker, so `readOnly` alone
                  does not suppress it. */}
              <Input
                type={headerLocked ? 'text' : 'date'}
                value={header.date}
                onChange={(_, d) => setH('date', d.value)}
                readOnly={headerLocked}
                appearance={headerLocked ? 'filled-darker' : undefined}
              />
            </Field>
            <Field label="Received Date">
              <Input value={formatNumericDate(invoice.received_at)} readOnly appearance="filled-darker" />
            </Field>
            <Field label="Subtotal" validationState={totalErr ? 'error' : 'none'}>
              {/* `type` swaps to `text` while locked — `readOnly` alone does not
                  suppress a number input's native spinners in every browser. */}
              <Input
                type={headerLocked ? 'text' : 'number'}
                contentBefore="$"
                value={header.subtotal}
                onChange={(_, d) => setH('subtotal', d.value)}
                onBlur={() => { if (headerLocked) return; const f = money2(header.subtotal); if (f !== header.subtotal) setH('subtotal', f) }}
                readOnly={headerLocked}
                appearance={headerLocked ? 'filled-darker' : undefined}
              />
            </Field>
            <Field label="Tax">
              <Input
                type={headerLocked ? 'text' : 'number'}
                contentBefore="$"
                value={header.tax}
                onChange={(_, d) => setH('tax', d.value)}
                onBlur={() => { if (headerLocked) return; const f = money2(header.tax); if (f !== header.tax) setH('tax', f) }}
                readOnly={headerLocked}
                appearance={headerLocked ? 'filled-darker' : undefined}
              />
            </Field>
            <Field label="Total" validationState={totalErr ? 'error' : 'none'}>
              <Input
                type={headerLocked ? 'text' : 'number'}
                contentBefore="$"
                value={header.total}
                onChange={(_, d) => setH('total', d.value)}
                onBlur={() => { if (headerLocked) return; const f = money2(header.total); if (f !== header.total) setH('total', f) }}
                readOnly={headerLocked}
                appearance={headerLocked ? 'filled-darker' : undefined}
              />
            </Field>
            <Field label="Currency" validationState={currencyErr ? 'error' : 'none'}>
              <Input
                value={header.currency}
                onChange={(_, d) => setH('currency', d.value)}
                readOnly={headerLocked}
                appearance={headerLocked ? 'filled-darker' : undefined}
              />
            </Field>
            {/* AI notes render at BOTH steps this editor covers — Import
                Control settles the Company, AP/AR verifies the vendor and the
                amounts against the document, and the notes are evidence for
                either job (orchestrator ruling, 2026-08-12, superseding the
                Import-Control-only gate this used to carry: `step` is always
                one of the two DecisionStep values this component mounts
                with, so no further condition is needed). Kept off any future
                read-only reuse of this modal at later steps, since this block
                only exists inside the editable form. When both note fields
                are present the extraction/matching distinction is kept; a
                single note is just "AI notes". */}
            {(invoice.ai_extracting_notes || invoice.ai_matching_notes) && (
                <div className={s.aiNotes}>
                  {invoice.ai_extracting_notes && (
                    <Caption1>
                      {invoice.ai_matching_notes ? 'AI extraction notes' : 'AI notes'}: {invoice.ai_extracting_notes}
                    </Caption1>
                  )}
                  {invoice.ai_matching_notes && (
                    <Caption1 className={invoice.ai_extracting_notes ? s.aiNoteGap : undefined}>
                      {invoice.ai_extracting_notes ? 'AI matching notes' : 'AI notes'}: {invoice.ai_matching_notes}
                    </Caption1>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      <Divider />
      <Subtitle2>Line Items Summary</Subtitle2>
      <div className={s.tableScroll}>
      <Table size="small" aria-label="Editable line items" className={s.lineItemsTable}>
        <TableHeader>
          <TableRow>
            {/* Sized columns total 710px; Description takes what's left, with a
                230px floor from the table's own minWidth (QA Q17). */}
            <TableHeaderCell style={{ width: 30 }}>#</TableHeaderCell>
            <TableHeaderCell style={{ width: 150 }}>Company</TableHeaderCell>
            <TableHeaderCell>Description</TableHeaderCell>
            {/* "What is it?", never "Account". Import Control settles the
                expense-type half ONLY — the cost center is deliberately not
                asked here (it belongs to whoever knows which discipline the
                spend was for), so there is no whole account on this screen to
                name. The word `Account` is reserved for the surfaces that
                actually hold one: Export Control, Manual Entry, Closing
                Confirmation, Denial Review and Archives (owner, 2026-08-10). */}
            <TableHeaderCell style={{ width: 190 }}>What is it?</TableHeaderCell>
            <TableHeaderCell style={{ width: 96 }}>Price</TableHeaderCell>
            <TableHeaderCell style={{ width: 76 }}>Quantity</TableHeaderCell>
            <TableHeaderCell style={{ width: 104 }}>Total</TableHeaderCell>
            <TableHeaderCell style={{ width: 64 }} aria-label="Line actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.lineItemId}>
              <TableCell>{r.sequence ?? '—'}</TableCell>
              <TableCell>
                {/* Per-line company leads the data columns (planning §4).
                    Untouched lines show (and save as) the invoice's own
                    company; picking one pins the line — exported into that
                    company's PCC file, and it moves the invoice's own company
                    too (the Company field above says how).

                    NO in-cell label: the table header one column up already
                    says "Company", and repeating it on every row was the same
                    word twice in the same column (owner, 2026-08-10). The
                    accessible name comes from the header cell instead. */}
                <EntityPicker
                  size="small"
                  ariaLabel="Company"
                  disabled={frozen}
                  value={
                    r.companyTouched
                      ? { id: r.companyId, name: r.companyName }
                      : { id: header.companyId, name: header.companyName }
                  }
                  placeholder="Inherits invoice company"
                  search={(t) =>
                    searchCompanies(t).then<EntityOption[]>((cs) =>
                      cs.map((c) => ({ id: c.company_id ?? '', primary: c.company_name ?? '(unnamed)' })),
                    )
                  }
                  onSelect={(o) => setRow(r.lineItemId, { companyId: o.id, companyName: o.primary, companyTouched: true })}
                />
              </TableCell>
              <TableCell>
                <Input
                  className={s.cellInput}
                  size="small"
                  value={r.description}
                  onChange={(_, d) => setRow(r.lineItemId, { description: d.value })}
                  readOnly={headerLocked}
                  appearance={headerLocked ? 'filled-darker' : undefined}
                />
              </TableCell>
              {/* Any line without an account is tinted, flag row or not — an
                  added line has no flag but still blocks release (QA Q12).
                  Suppressed while locked (Import Control): the tint is noise on
                  a control the reviewer cannot touch here, and the Flags panel
                  caption above carries the message instead. */}
              {/* Any line without an EXPENSE TYPE is tinted — never a line
                  merely awaiting its cost center, which is normal here and is
                  nobody at this step's business. */}
              <TableCell className={!headerLocked && !r.expenseTypeCode ? s.cellFlagged : undefined}>
                <div className={s.accountCell}>
                  <div className={s.accountPicker}>
                    <EntityPicker
                      size="small"
                      // NO in-cell label: the column header asks the question
                      // now, on this screen and at the approval tiers alike
                      // (owner, 2026-08-10). The accessible name comes from the
                      // header cell.
                      ariaLabel="What is it?"
                      disabled={headerLocked}
                      // `expenseTypeLabel` is set only by a pick in this session
                      // — a line loaded from the server carries the CODE and
                      // nothing else, because the wire has no label field. Falling
                      // back to the master lookup is what stops an already-coded
                      // line rendering an EMPTY box: not tinted, not blocking
                      // release, and reading as uncoded while it is in fact fine.
                      value={{
                        id: r.expenseTypeCode,
                        name: r.expenseTypeLabel ?? labels.expenseType(r.expenseTypeCode),
                      }}
                      // The label above now names the question — the default
                      // "Search…" placeholder says what to do rather than
                      // repeating it, same treatment as the approval tiers'
                      // picker.
                      search={(t) =>
                        searchExpenseTypes(t).then<EntityOption[]>((es) =>
                          es.map((e) => ({
                            id: e.expense_type_code,
                            primary: e.label,
                            secondary: e.expense_type_code,
                          })),
                        )
                      }
                      onSelect={(o) =>
                        setRow(r.lineItemId, {
                          expenseTypeCode: o.id,
                          expenseTypeLabel: o.primary,
                        })
                      }
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Input
                  className={s.cellInput}
                  size="small"
                  type={headerLocked ? 'text' : 'number'}
                  contentBefore="$"
                  value={r.price}
                  onChange={(_, d) => setRow(r.lineItemId, { price: d.value })}
                  onBlur={() => { if (headerLocked) return; const f = money2(r.price); if (f !== r.price) setRow(r.lineItemId, { price: f }) }}
                  readOnly={headerLocked}
                  appearance={headerLocked ? 'filled-darker' : undefined}
                />
              </TableCell>
              <TableCell>
                <Input
                  className={s.cellInput}
                  size="small"
                  type={headerLocked ? 'text' : 'number'}
                  value={r.quantity}
                  onChange={(_, d) => setRow(r.lineItemId, { quantity: d.value })}
                  readOnly={headerLocked}
                  appearance={headerLocked ? 'filled-darker' : undefined}
                />
              </TableCell>
              <TableCell>
                <Input
                  className={s.cellInput}
                  size="small"
                  type={headerLocked ? 'text' : 'number'}
                  contentBefore="$"
                  value={r.total}
                  onChange={(_, d) => setRow(r.lineItemId, { total: d.value })}
                  onBlur={() => { if (headerLocked) return; const f = money2(r.total); if (f !== r.total) setRow(r.lineItemId, { total: f }) }}
                  readOnly={headerLocked}
                  appearance={headerLocked ? 'filled-darker' : undefined}
                />
              </TableCell>
              <TableCell>
                {!headerLocked && (
                  <div className={s.rowActions}>
                    {/* Per-line entity tagging (AE4) lives in a popover off this
                        button — it used to render open under every line item row,
                        which cluttered the table (user note 2026-07-14). Only saved
                        lines have a real id to hang entity rows on; a
                        just-added line shows a hint until it's saved. */}
                    <Popover mountNode={dialogEl ?? undefined}>
                      <PopoverTrigger disableButtonEnhancement>
                        <Button appearance="subtle" size="small" icon={<LinkRegular />} aria-label="Entities this line is for" title="Entities this line is for" />
                      </PopoverTrigger>
                      <PopoverSurface className={s.entityPopSurface}>
                        <div className={s.entityPop}>
                          {r.isNew ? (
                            <Caption1 className={s.entityHint}>Save the invoice to tag entities on this line.</Caption1>
                          ) : (
                            <AssociatedEntitiesEditor
                              invoice={invoice}
                              lineItem={lineItemsById.get(r.lineItemId) ?? ({ id: r.lineItemId } as InvoiceLineItem)}
                            />
                          )}
                        </div>
                      </PopoverSurface>
                    </Popover>
                    <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="Remove line item" onClick={() => removeRow(r.lineItemId)} />
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
      {!headerLocked && (
      <div className={s.addRow}>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addRow}>
          Add line item
        </Button>
      </div>
      )}

      {/* Accounts Summary (planning §4) — grouped by EXPENSE TYPE here, because
          neither step this form covers ever shows a cost center (Import
          Control and AP/AR each hold the expense-type half only). Dynamic:
          recomputes from the same in-memory row state the table edits, so
          per-account totals track as the reviewer swaps a cost center.
          Effective per-line company: a pinned line keeps its own, an
          untouched line follows the header. The record's company is the
          resolved one, so the per-company breakdown appears exactly when the
          lines are not all on the invoice's company.

          Hidden entirely at Import Control (owner item 3, 2026-08-13): a pure
          rollup of a screen whose only live control is the per-line Company
          picker adds nothing that screen's reviewer acts on. AP/AR keeps it. */}
      {!atImportControl && (
      <AccountsSummary
        recordCompanyId={companyId}
        groupBy="expense_type"
        subtotal={header.subtotal}
        tax={header.tax}
        total={header.total}
        rows={rows.map((r) => ({
          expenseTypeCode: r.expenseTypeCode,
          // Same fallback as the picker above: without it the summary degraded to
          // raw codes for every line the reviewer had not re-picked this session,
          // which is the opposite of "the Facility Manager reads words".
          expenseTypeLabel: r.expenseTypeLabel ?? labels.expenseType(r.expenseTypeCode),
          companyId: r.companyTouched ? r.companyId : header.companyId,
          companyName: r.companyTouched ? r.companyName : header.companyName,
          total: toNumber(r.total),
        }))}
      />
      )}
    </div>
  )
}
